import { INVALID_STRING_PATTERN, LANGUAGE_CODES } from "./default-returner";
import {
  InvalidDataTypes,
  InvalidDataTypesDetails,
} from "@/components/PImport/types/InvalidItem";
import { DataType } from "@/types/enums/DataType";
import { Field } from "@/types/Field";
import { isGuestFieldBlockedOnUpdate } from "@/lib/guests/guestTypeMutability";
import { isMailOptInField, parseImportOptIn } from "./import-boolean-words";

/**
 * Validates the password and returns the validation state.
 * @param password - The password string to validate.
 * @returns PasswordValidationState object indicating which validations passed.
 */
export function validatePassword(password: string) {
  return {
    length: password?.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    specialChar: /[^a-zA-Z0-9]/.test(password),
  };
}

export function validatePasswordBasic(
  password: string,
  t: (key: string) => string,
) {
  if (!password) {
    return t("passwordRequired");
  }
  if (password?.length < 8) {
    return t("passwordMinLength");
  }
  return "";
}

/**
 * Validates a MongoDB ObjectId
 * MongoDB ObjectIds are exactly 24 hexadecimal characters
 * @param value - The value to validate
 * @param t - Translation function for error messages
 * @returns Error message if invalid, empty string if valid
 */
export const validateMongoObjectId = (value: string): string => {
  // Empty is allowed if the field is optional
  if (!value || value.trim() === "") {
    return "";
  }

  // MongoDB ObjectId: exactly 24 hexadecimal characters
  const objectIdRegex = /^[a-fA-F0-9]{24}$/;

  if (!objectIdRegex.test(value)) {
    return "invalidInviteCode";
  }

  return "";
};

/**
 * Validates if the password and confirm password match.
 * @param password - The password string.
 * @param confirmPassword - The confirm password string.
 * @returns A string with the error message or an empty string if valid.
 */
export function validatePasswordEquality(
  password: string,
  confirmPassword: string,
  t: (key: string) => string,
) {
  if (password !== confirmPassword) {
    return t("passwordNoMatch");
  }
  return "";
}

/**
 * Validates the email and returns a specific error message if validation fails.
 * @param email - The email string to validate.
 * @returns A string with the error message or an empty string if valid.
 */
export function validateEmail(
  email: string,
  t: (key: string) => string,
): string {
  if (!email) {
    return t("emailRequired");
  }
  if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
    return t("emailInvalid");
  }
  if (email?.length < 5 || email?.length > 254) {
    return t("emailInvalid");
  }
  return ""; // No errors
}

/**
 * Validates a text field and returns a specific error message if validation fails.
 * @param input - The text string to validate.
 * @returns A string with the error message or an empty string if valid.
 */
export function validateText(
  input: string,
  t: (key: string) => string,
): string {
  if (!input.trim()) {
    return t("fieldRequired");
  }
  if (input?.length < 2) {
    return t("atLeastTwoChars");
  }
  if (input?.length > 50) {
    return t("maxFiftyChars");
  }

  // Check for invalid characters (emojis and control characters)
  const invalidCharPattern =
    /[\u{1F000}-\u{1F9FF}\u{2600}-\u{27BF}\u0000-\u001F\u007F-\u009F]/u;

  if (invalidCharPattern.test(input)) {
    return t("invalidCharacters");
  }
  return ""; // No errors
}

export function validateOtp(otpArray: string[], t: (key: string) => string) {
  const otpCode = otpArray.join("");
  if (otpCode?.length < 6) {
    return t("otpIncomplete"); // Message: "OTP is incomplete"
  }
  return ""; // No errors
}

export function isValidToken(
  token: string | undefined,
  buffertime: number,
): boolean {
  if (!token) return false;

  // First check if the token has the basic JWT structure
  if (!/^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(token)) {
    return false;
  }

  try {
    // Then check the expiration
    const payloadBase64 = token.split(".")[1];
    const payload = JSON.parse(
      new TextDecoder().decode(Uint8Array.from(atob(payloadBase64.replace(/-/g, "+").replace(/_/g, "/")), c => c.charCodeAt(0))),
    );

    // Check if the token is about to expire in the next minute
    const currentTime = Math.floor(Date.now() / 1000); // Convert to seconds for JWT
    const bufferTime = buffertime; // 1 minute in seconds

    return payload.exp > currentTime + bufferTime;
  } catch {
    // If there's any error parsing the token, consider it invalid
    return false;
  }
}

export function isValidLanguageCode(langCode: string): boolean {
  return LANGUAGE_CODES.includes(langCode);
}

// --- Merged from input-validator-bool.ts ---

export function isEditableFieldGuest(
  field: Field,
  acceptanceState?: string | null,
): boolean {
  // Fields the backend refuses on the update path. Kept out of the array below
  // so the decision lives in ONE place (guestTypeMutability.ts) rather than as
  // a string hunt in a list.
  //
  // `acceptanceState` is the ROW's — `guestType` is not writable while that
  // guest's request is undecided. Callers that have a row must pass it; the
  // guard fails open without it, which is what the pin in
  // guestTypeMutability.test.ts exists for.
  if (
    isGuestFieldBlockedOnUpdate(field.name, field.additionalField, acceptanceState)
  ) {
    return false;
  }
  // IMAGE additional fields ARE list-editable, but they bypass the pending-
  // edit batch entirely: TCCImage persists immediately (presign→confirm on
  // upload / host-driven single-guest "" update on remove) via the
  // onValuePersisted / onImmediateRemove channels GuestsRows threads in —
  // onChangeValue (the batch feeder) is never called for IMAGE cells there,
  // so no stale additionalFields echo can poison the batch.
  const nonEditableFields = [
    "checkedInAt",
    "checkedIn",
    "archived",
    "eventId",
    "guestId",
    "createdAt",
    "updatedAt",
    "checkedIn",
    "checkedInAt",
    "numCompanions",
    "seat",
  ];
  return !nonEditableFields.includes(field.name);
}

export function isEditableFieldEvent(field: Field): boolean {
  if (field.additionalField) return false;
  const nonEditableFields = [
    "guestsTotal",
    "guestsUncontacted",
    "guestsAccepted",
    "guestsDeclined",
    "guestsContacted",
    "guestsWaitlist",
    "guestsCheckedIn",
    "companyName",
    "eventId",
    "guestId",
    "createdAt",
    "updatedAt",
  ];
  return !nonEditableFields.includes(field.name);
}

export function isValidPassword(password: string): boolean {
  if (!password) return false;
  const validation = {
    length: password?.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    specialChar: /[^a-zA-Z0-9]/.test(password),
  };
  return (
    validation?.length &&
    validation.uppercase &&
    validation.lowercase &&
    validation.specialChar
  );
}

export const isValidEmailWithVariable = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
};

export function isValidEmail(email: string): boolean {
  if (!email) return false;
  if (email?.length < 5 || email?.length > 254) return false;
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
}

/**
 * Canonical form for account emails: trimmed + lowercased. The backend
 * normalizes the same way on every auth read/write; normalizing here keeps
 * payloads, cookies and local dedup checks consistent with stored data.
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Every character we treat as "edge whitespace" on a stored field value.
 *
 * Deliberately explicit instead of `String.prototype.trim()`: the same
 * normalization has to exist on the Java side, and neither built-in covers the
 * set on its own — Java's `trim()` stops at U+0020 and `strip()`
 * (`Character.isWhitespace`) excludes NBSP, while JS `\s` excludes U+200B.
 *
 * Which half applies where matters, because it is easy to over-claim: the NBSP
 * argument is the JAVA one (prod carried 128 NBSP edges from Excel/Word paste,
 * and neither Java built-in removes them). Here in TypeScript
 * `String.prototype.trim()` already strips NBSP, U+FEFF, U+2007, U+202F,
 * U+1680, U+2000-200A and U+3000 — measured — so the only character this class
 * gains over `.trim()` is **U+200B**. A test covering only NBSP cannot tell the
 * two apart on this side. Keep the class byte-for-byte in sync with the backend
 * helper.
 */
const EDGE_WS_CLASS =
  "[\\t\\n\\v\\f\\r \\u00a0\\u1680\\u2000-\\u200a\\u200b\\u2028\\u2029\\u202f\\u205f\\u3000\\ufeff]";
const EDGE_WHITESPACE = new RegExp(
  `^${EDGE_WS_CLASS}+|${EDGE_WS_CLASS}+$`,
  "g",
);

/**
 * Canonical form for a stored guest/contact field value: leading and trailing
 * whitespace removed, everything inside left alone.
 *
 * Same doctrine as {@link normalizeEmail} — the filter lane already trims the
 * operand it sends (`parseFiltersApiRequest`), so a value stored with an edge
 * space is unreachable by `=` (Mongo `$eq`) forever, with no input the user
 * could type to match it. `~` still matches, which is why the bug reads as
 * "exact search is broken" rather than "data is dirty".
 *
 * INTERNAL whitespace is content, never collapsed: "Gänsemarkt 21-23" must
 * survive untouched. A value that is nothing but whitespace becomes "".
 */
export function normalizeFieldValue(value: string): string {
  return value.replace(EDGE_WHITESPACE, "");
}

export function isTextValid(input: string): boolean {
  if (!input.trim() || input?.length < 1 || input?.length > 50) {
    return false;
  }
  const invalidCharPattern =
    /[\u{1F000}-\u{1F9FF}\u{2600}-\u{27BF}\u0000-\u001F\u007F-\u009F]/u;
  if (invalidCharPattern.test(input)) {
    return false;
  }
  return true;
}

export function isValidOtp(otp: string): boolean {
  if (otp?.length !== 6) return false;
  return true;
}

export const needsDataRefetch = (changedAttributes: string[]): boolean => {
  const fieldsToCheck = ["theme", "preSelector", "filter", "fields"];
  return fieldsToCheck.some((field) => changedAttributes.includes(field));
};

export function isValidObjectId(id: string): boolean {
  return /^[0-9a-fA-F]{24}$/.test(id);
}

// Boolean check matching backend INVALID_STRING_PATTERN + length rules:
// non-empty trimmed string, ≤ max chars, no emojis or control chars.
// Domain-specific validators wrap this with their own i18n message keys.
export function isValidEntityName(
  input: string | undefined,
  max = 70,
): boolean {
  const trimmed = (input ?? "").trim();
  return (
    trimmed.length >= 1 &&
    trimmed.length <= max &&
    !INVALID_STRING_PATTERN.test(trimmed)
  );
}

function firstInvalidOptInCell(
  values: (string | null)[],
): InvalidDataTypesDetails | null {
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (v === null || v === undefined || v.trim() === "") continue;
    if (parseImportOptIn(v) === null) {
      return { index: i, value: v, invalidChar: null };
    }
  }
  return null;
}

export function getInvalidDetails(
  field: Field,
  InvalidDataTypes: InvalidDataTypes,
  values: (string | null)[],
): InvalidDataTypesDetails | null {
  switch (field.dataType) {
    case DataType.BOOLEAN:
      // The column detector reads every BOOLEAN column alike; the mail opt-in
      // reads an ambiguous word ("jo") as a NO, so it is no warning there
      // (audit-14 M-1) — the same rule as the row builder.
      return isMailOptInField(field)
        ? firstInvalidOptInCell(values)
        : InvalidDataTypes.boolean;
    case DataType.DATE:
      return InvalidDataTypes.date;
    case DataType.DATE_TIME:
      return InvalidDataTypes.dateTime;
    case DataType.CURRENCY:
    case DataType.DOUBLE:
      return InvalidDataTypes.double;
    case DataType.EMAIL:
      return InvalidDataTypes.email;
    case DataType.URL:
      return InvalidDataTypes.url;
    case DataType.LONG:
      return InvalidDataTypes.long;
    case DataType.STRING:
      return InvalidDataTypes.string;
    case DataType.OBJECT_ID:
      return InvalidDataTypes.objectId;
    case DataType.SINGLE_SELECT:
      return isValidSingleSelect(field.options ?? [], values);
    case DataType.MULTI_SELECT:
      return isValidMultiSelect(field.options ?? [], values);
    case DataType.IMAGE:
      // IMAGE is excluded as an import mapping target (useMapper) — values are
      // set exclusively via the presign→confirm upload flow, never typed or
      // imported. No column-value check applies; explicit case so a future
      // "fail-open default" refactor doesn't silently start accepting it.
      return null;
    default:
      return null;
  }
}

export function isValidSingleSelect(
  options: string[],
  values: (string | null)[],
): InvalidDataTypesDetails | null {
  const validOptionsSet = new Set(options);
  for (let i = 0; i < values?.length; i++) {
    const value = values[i];
    if (value === null || value.trim() === "") continue;
    if (!validOptionsSet.has(value)) {
      return { index: i, value, invalidChar: null };
    }
  }
  return null;
}

export function isValidMultiSelect(
  options: string[],
  values: (string | null)[],
): InvalidDataTypesDetails | null {
  const validOptionsSet = new Set(options);
  for (let i = 0; i < values?.length; i++) {
    const value = values[i];
    if (value === null || value.trim() === "") continue;
    const multiValues = value.split(",").map((v) => v.trim());
    for (const multiValue of multiValues) {
      if (multiValue === "") continue;
      if (!validOptionsSet.has(multiValue)) {
        return { index: i, value, invalidChar: null };
      }
    }
  }
  return null;
}

/**
 * Does a submitted additionalFields map carry a REAL value for this field?
 *
 * The TypeScript twin of `InputValidator.isFieldBlankOrMissing` — blank-aware
 * and case-insensitive, exactly as the backend is.
 *
 * WHY IT EXISTS. Five places in this app decided "did the user supply a value,
 * or should the configured default show?" with `field.name in additionalFields`
 * — a PRESENCE test. The backend answers the same question with a BLANK-OR-
 * MISSING test, so for a value of `""`, `"   "` or `[]` the two disagreed: the
 * drawer showed an empty box and the required-missing warning stayed silent,
 * while the backend quietly injected the default and stored it. The organizer
 * saw one thing and the database held another.
 *
 * The four blank shapes are the ones the blank contract names — `undefined`,
 * `null`, `""` and `[]` — plus a whitespace-only string, which normalizes to
 * `""`. See main-backend/docs/whitespace-wave-decision.md §2.
 *
 * READ THROUGH `resolveSuppliedFieldKey`, never through `values[field.name]`.
 * The lookup resolves case-insensitively, mirroring Java, so the key it found
 * may not be the spelling the caller holds. A caller that asks this function
 * and then reads the exact name gets `undefined` for a field that IS supplied —
 * which flips a controlled input to uncontrolled mid-render and makes the three
 * condition-seeding callers treat the field as absent.
 */
export function resolveSuppliedFieldKey(
  values: Record<string, unknown> | null | undefined,
  name: string,
): string | undefined {
  if (values === null || values === undefined) return undefined;
  // `hasOwnProperty.call`, never `in`: `in` walks the PROTOTYPE CHAIN, so
  // `"constructor" in {}` is true for every object and a custom field named
  // `constructor` / `toString` / `valueOf` / `hasOwnProperty` resolved to a
  // native function. Field names are organizer-chosen. Java's twin
  // (`InputValidator.isFieldBlankOrMissing`) uses `map.containsKey`, which has
  // never had this hazard — this was the frontend drifting from it.
  if (Object.prototype.hasOwnProperty.call(values, name)) return name;
  return Object.keys(values).find((k) => k.toLowerCase() === name.toLowerCase());
}

export function hasSuppliedFieldValue(
  values: Record<string, unknown> | null | undefined,
  name: string,
): boolean {
  const key = resolveSuppliedFieldKey(values, name);
  if (key === undefined || values === null || values === undefined) return false;
  const v = values[key];
  if (v === null || v === undefined) return false;
  // A function is never a value a user supplied — belt for the prototype case
  // above, and for any loosely-typed caller.
  if (typeof v === "function") return false;
  if (typeof v === "string") return normalizeFieldValue(v) !== "";
  if (Array.isArray(v)) return v.length > 0;
  return true;
}
